import {
  type DatabaseBlockDataSource,
  databaseViewInitTemplate,
  provisionShardedDatabaseBlockDataSource,
} from '@blocksuite/affine/blocks/database';
import {
  type Doc,
  type ExtensionType,
  type Store,
  Text,
  type Workspace,
} from '@blocksuite/affine/store';
import { parseXYWH, serializeXYWH } from '@blocksuite/global/gfx';
import {
  type CanvasPlacementRect,
  DEFAULT_CANVAS_DATABASE_HEIGHT,
  DEFAULT_CANVAS_DATABASE_WIDTH,
  DEFAULT_CANVAS_PLACEMENT_GAP,
  findFreeCanvasRowRects,
} from '@streamlain/database-identity';
import type { LocalPublicationReceipt } from '@streamlain/doc-publication-contract';

import type {
  DatabaseImportColumnPayload,
  DatabaseImportRowPayload,
  DatabaseImportSourcePayload,
} from './database-import-contracts';
import type { DatabaseImportSourceMetadata } from './database-import-payload';
import type { DatabaseImportSyncProgress } from './database-import-sync-progress';

type SourceWithLayout = DatabaseImportSourcePayload & {
  width?: number;
  height?: number;
};

type SourceLayoutInput = {
  x: number;
  y: number;
  width?: number;
  height?: number;
};

export type LocalShardedDatabaseImportSourceMetadata =
  DatabaseImportSourceMetadata;

export type LocalShardedDatabaseImportRowBatch = {
  clientId: string;
  rows: DatabaseImportRowPayload[];
};

export type LocalShardedDatabaseImportOptions = {
  collection: Workspace;
  docId: string;
  sources: SourceWithLayout[];
  extensions?: ExtensionType[];
  docName?: string;
  onProgress?: LocalShardedDatabaseImportProgressChange;
  /** Internal counters of this import; never a rendering barrier. */
  syncProgress?: DatabaseImportSyncProgress;
  signal?: AbortSignal;
};

export type LocalShardedDatabaseImportResult = {
  docId: string;
  doc: Doc;
  stats: {
    databaseCount: number;
    totalRows: number;
  };
  databaseBlockIds: string[];
  receipts: readonly LocalPublicationReceipt[];
};

export type LocalShardedDatabaseImportFromBatchesOptions = Omit<
  LocalShardedDatabaseImportOptions,
  'sources'
> & {
  sources: LocalShardedDatabaseImportSourceMetadata[];
  readNextBatch: () => Promise<LocalShardedDatabaseImportRowBatch | null>;
};

export type LocalShardedDatabaseImportProgressPhase =
  | 'csv-reading'
  | 'excel-reading'
  | 'notion-reading'
  | 'databases-preparing'
  | 'rows-importing'
  | 'import-finalizing'
  | 'import-cancelling';

export type LocalShardedDatabaseImportProgress = {
  phase: LocalShardedDatabaseImportProgressPhase;
  processedRows: number;
  totalRows: number;
  percent: number | null;
};

export type LocalShardedDatabaseImportProgressChange = (
  progress: LocalShardedDatabaseImportProgress
) => void;

const TITLE_COLUMN_ID = 'title';
const PROGRESS_UPDATE_ROW_INTERVAL = 25;

type BlockModelWithGeometry = {
  props?: { xywh?: unknown };
  yBlock?: { get?: (key: string) => unknown };
  xywh?: unknown;
};

function isLoadedImportTargetDoc(doc: Doc): boolean {
  return (doc as Doc & { ready?: boolean }).ready === true;
}

function progressPercent(processedRows: number, totalRows: number) {
  if (totalRows <= 0) return null;
  return Math.min(100, Math.floor((processedRows / totalRows) * 100));
}

function toCanvasPlacementRect(value: unknown): CanvasPlacementRect | null {
  const xywh = parseXYWH(value);
  if (!xywh || xywh[2] <= 0 || xywh[3] <= 0) return null;
  return [xywh[0], xywh[1], xywh[2], xywh[3]];
}

function readCanvasBlockRect(model: unknown): CanvasPlacementRect | null {
  if (!model || typeof model !== 'object') return null;
  const block = model as BlockModelWithGeometry;
  return (
    toCanvasPlacementRect(block.xywh) ??
    toCanvasPlacementRect(block.props?.xywh) ??
    toCanvasPlacementRect(block.yBlock?.get?.('prop:xywh'))
  );
}

function collectOccupiedCanvasBlockRects(store: Store): CanvasPlacementRect[] {
  const root = store.root as { children?: unknown[] } | null;
  const occupied: CanvasPlacementRect[] = [];
  for (const child of root?.children ?? []) {
    const rect = readCanvasBlockRect(child);
    if (rect) occupied.push(rect);
  }
  return occupied;
}

function preferredImportPosition(
  source: SourceLayoutInput
): { x: number; y: number } | undefined {
  if (!Number.isFinite(source.x) || !Number.isFinite(source.y)) {
    return undefined;
  }
  return { x: source.x, y: source.y };
}

function resolveImportSourceCanvasRects(
  sources: readonly SourceLayoutInput[],
  occupied: CanvasPlacementRect[]
): CanvasPlacementRect[] {
  const rects = findFreeCanvasRowRects({
    occupied,
    items: sources.map(source => ({
      width: source.width ?? DEFAULT_CANVAS_DATABASE_WIDTH,
      height: source.height ?? DEFAULT_CANVAS_DATABASE_HEIGHT,
    })),
    gap: DEFAULT_CANVAS_PLACEMENT_GAP,
    preferred: sources[0] ? preferredImportPosition(sources[0]) : undefined,
  });
  occupied.push(...rects);
  return rects;
}

export function throwIfLocalDatabaseImportAborted(signal?: AbortSignal): void {
  if (!signal?.aborted) return;
  const error = new Error('Import was cancelled.');
  error.name = 'AbortError';
  throw error;
}

function ensureHostStore(input: {
  collection: Workspace;
  docId: string;
  docName?: string;
  extensions?: ExtensionType[];
}): { doc: Doc; store: Store; parentId: string } {
  const doc =
    input.collection.getDoc(input.docId) ??
    input.collection.createDoc(input.docId);
  const store = doc.getStore({
    id: input.docId,
    ...(input.extensions ? { extensions: input.extensions } : {}),
  });
  if (!isLoadedImportTargetDoc(doc)) {
    store.load?.();
  }

  if (!store.root) {
    const parentId = store.addBlock('affine:page', {
      title: new Text(input.docName ?? ''),
    });
    store.addBlock('affine:surface' as never, {}, parentId);
  }

  const parentId = store.root?.id;
  if (!parentId) {
    throw new Error('Database import target document has no root block.');
  }

  return { doc, store, parentId };
}

type SourceMetadataWithLayout =
  | SourceWithLayout
  | LocalShardedDatabaseImportSourceMetadata;

function databaseIdsByClientId(
  store: Store,
  sources: readonly SourceMetadataWithLayout[]
): Map<string, string> {
  const ids = new Map<string, string>();
  for (const source of sources) {
    if (ids.has(source.clientId)) {
      throw new Error(
        `Duplicate database import clientId "${source.clientId}"`
      );
    }
    ids.set(source.clientId, store.workspace.idGenerator());
  }
  return ids;
}

function resolveColumnData(
  column: DatabaseImportColumnPayload,
  databaseIdByClientId: ReadonlyMap<string, string>
): Record<string, unknown> | undefined {
  if (!column.data) return undefined;
  const data = { ...column.data };
  if (column.type !== 'relation') return data;

  const targetClientId =
    typeof data.targetDatabaseClientId === 'string'
      ? data.targetDatabaseClientId
      : undefined;
  const targetDatabaseId =
    typeof data.targetDatabaseId === 'string'
      ? data.targetDatabaseId
      : targetClientId
        ? databaseIdByClientId.get(targetClientId)
        : undefined;
  if (!targetDatabaseId) {
    throw new Error(
      `Relation column "${column.name}" is missing targetDatabaseId or targetDatabaseClientId.`
    );
  }

  delete data.targetDatabaseClientId;
  return {
    ...data,
    targetDatabaseId,
    targetFieldId:
      typeof data.targetFieldId === 'string'
        ? data.targetFieldId
        : TITLE_COLUMN_ID,
    cardinality:
      data.cardinality === 'one' || data.cardinality === 'many'
        ? data.cardinality
        : 'many',
  };
}

function configureColumns(input: {
  dataSource: DatabaseBlockDataSource;
  source: Pick<SourceMetadataWithLayout, 'titleColumnId' | 'columns'>;
  databaseIdByClientId: ReadonlyMap<string, string>;
}): Map<string, string> {
  const columnIds = new Map<string, string>();
  columnIds.set(input.source.titleColumnId, TITLE_COLUMN_ID);

  for (const column of input.source.columns) {
    if (column.id === input.source.titleColumnId) {
      // The title column keeps the name the source gives it, stored like a
      // rename of the title column; the default name stores nothing.
      if (
        column.name.trim() &&
        column.name !== input.dataSource.propertyNameGet(TITLE_COLUMN_ID)
      )
        input.dataSource.propertyNameSet(TITLE_COLUMN_ID, column.name);
      continue;
    }
    const propertyId = input.dataSource.propertyAdd('end', {
      type: column.type,
      name: column.name,
    });
    if (!propertyId) {
      throw new Error(
        `Database import cannot create column "${column.name}" of type "${column.type}".`
      );
    }
    columnIds.set(column.id, propertyId);

    const data = resolveColumnData(column, input.databaseIdByClientId);
    if (data) {
      input.dataSource.propertyDataSet(propertyId, data);
    }
  }

  return columnIds;
}

function extractCellValue(value: unknown): unknown {
  if (
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'value' in value
  ) {
    return (value as { value: unknown }).value;
  }
  return value;
}

function textValue(value: unknown): string {
  const raw = extractCellValue(value);
  if (raw == null) return '';
  if (typeof raw === 'string') return raw;
  if (
    raw &&
    typeof raw === 'object' &&
    '$blocksuite:internal:text$' in raw &&
    Array.isArray((raw as { delta?: unknown }).delta)
  ) {
    return (raw as unknown as { delta: Array<{ insert?: unknown }> }).delta
      .map(delta => String(delta.insert ?? ''))
      .join('');
  }
  if (typeof (raw as { toString?: unknown }).toString === 'function') {
    return String(raw);
  }
  return '';
}

function valueForProperty(input: {
  propertyId: string;
  propertyType: string | undefined;
  value: unknown;
}): unknown {
  if (
    input.propertyId === TITLE_COLUMN_ID ||
    input.propertyType === 'title' ||
    input.propertyType === 'rich-text'
  ) {
    return textValue(input.value);
  }
  return extractCellValue(input.value);
}

function prepareRowBatch(input: {
  dataSource: DatabaseBlockDataSource;
  rows: readonly DatabaseImportRowPayload[];
  columnIds: ReadonlyMap<string, string>;
  signal?: AbortSignal;
}) {
  return input.rows.map(row => {
    throwIfLocalDatabaseImportAborted(input.signal);
    const values: Record<string, unknown> = {};
    for (const [sourceColumnId, rawValue] of Object.entries(row.values)) {
      const propertyId = input.columnIds.get(sourceColumnId);
      if (!propertyId) {
        throw new Error(
          `Row "${row.id}" references unknown import column "${sourceColumnId}".`
        );
      }
      values[propertyId] = valueForProperty({
        propertyId,
        propertyType: input.dataSource.propertyTypeGet(propertyId),
        value: rawValue,
      });
    }
    return { rowId: row.id, values };
  });
}

async function writeRowBatch(input: {
  dataSource: DatabaseBlockDataSource;
  rows: readonly DatabaseImportRowPayload[];
  columnIds: ReadonlyMap<string, string>;
  totalRows: number;
  processedRows: number;
  onProgress?: LocalShardedDatabaseImportProgressChange;
  syncProgress?: DatabaseImportSyncProgress;
  signal?: AbortSignal;
}): Promise<number> {
  const rowInserts = prepareRowBatch(input);
  input.syncProgress?.local(
    input.processedRows + input.rows.length,
    input.processedRows
  );

  input.dataSource.rowAddManyWithCellValues(rowInserts);
  // The next producer batch is admitted only after the shared row/root barrier.
  await input.dataSource.flushRowWrites(input.signal);
  throwIfLocalDatabaseImportAborted(input.signal);

  const processedRows = input.processedRows + input.rows.length;
  input.syncProgress?.local(processedRows, processedRows);
  if (
    input.onProgress &&
    (processedRows === input.totalRows ||
      Math.floor(processedRows / PROGRESS_UPDATE_ROW_INTERVAL) >
        Math.floor(input.processedRows / PROGRESS_UPDATE_ROW_INTERVAL))
  ) {
    // Progress is an observation of committed work, never a rendering barrier.
    input.onProgress({
      phase: 'rows-importing',
      processedRows,
      totalRows: input.totalRows,
      percent: progressPercent(processedRows, input.totalRows),
    });
    throwIfLocalDatabaseImportAborted(input.signal);
  }
  return input.rows.length;
}

async function writeRows(input: {
  dataSource: DatabaseBlockDataSource;
  source: Pick<DatabaseImportSourcePayload, 'rows'>;
  columnIds: ReadonlyMap<string, string>;
  totalRows: number;
  processedRows: number;
  onProgress?: LocalShardedDatabaseImportProgressChange;
  syncProgress?: DatabaseImportSyncProgress;
  signal?: AbortSignal;
}): Promise<number> {
  let written = 0;
  for (let offset = 0; offset < input.source.rows.length; offset += 250) {
    written += await writeRowBatch({
      dataSource: input.dataSource,
      rows: input.source.rows.slice(offset, offset + 250),
      columnIds: input.columnIds,
      totalRows: input.totalRows,
      processedRows: input.processedRows + written,
      onProgress: input.onProgress,
      syncProgress: input.syncProgress,
      signal: input.signal,
    });
  }
  return written;
}

/** What a database import writes; the canvas position belongs to its host. */
export type DatabaseImportSourceContent = Omit<
  DatabaseImportSourcePayload,
  'x' | 'y'
>;

type ImportStoreTarget = {
  databaseId: string;
  source: DatabaseImportSourceContent;
  parentId: string;
  props?: Record<string, unknown>;
};

type ImportStoreResult = {
  databaseBlockIds: string[];
  receipts: LocalPublicationReceipt[];
  writtenRows: number;
};

/**
 * Provisions each target database in `store` and writes its rows. A target
 * whose host block already sits in the store gets its storage; any other gets
 * a new host block below `parentId`.
 */
async function importSourcesIntoStore(input: {
  store: Store;
  targets: readonly ImportStoreTarget[];
  databaseIdByClientId: ReadonlyMap<string, string>;
  totalImportRows: number;
  processedRows: number;
  onProgress?: LocalShardedDatabaseImportProgressChange;
  syncProgress?: DatabaseImportSyncProgress;
  signal?: AbortSignal;
  onProvisioned?: (databaseId: string) => void;
}): Promise<ImportStoreResult> {
  const databaseBlockIds: string[] = [];
  const receipts: LocalPublicationReceipt[] = [];
  let processedRows = input.processedRows;
  for (const target of input.targets) {
    throwIfLocalDatabaseImportAborted(input.signal);
    const { source } = target;
    let columnIds = new Map<string, string>();
    const initialRows = source.rows.slice(0, 250);
    const provisioned = await provisionShardedDatabaseBlockDataSource(
      input.store,
      {
        databaseId: target.databaseId,
        title: source.title,
        parentId: target.parentId,
        signal: input.signal,
        props: target.props,
        initDataSource: dataSource => {
          databaseViewInitTemplate(dataSource, 'table');
          columnIds = configureColumns({
            dataSource,
            source,
            databaseIdByClientId: input.databaseIdByClientId,
          });
          dataSource.rowAddManyWithCellValues(
            prepareRowBatch({
              dataSource,
              rows: initialRows,
              columnIds,
              signal: input.signal,
            })
          );
        },
      }
    );
    if (provisioned.status === 'discarded') continue;
    input.onProvisioned?.(target.databaseId);
    if (provisioned.receipt) receipts.push(provisioned.receipt);
    input.syncProgress?.local(
      processedRows + initialRows.length,
      processedRows + initialRows.length
    );
    const scope = provisioned.dataSource.openPublicationScope();
    try {
      const sourceRows = await writeRows({
        dataSource: provisioned.dataSource,
        source: { ...source, rows: source.rows.slice(initialRows.length) },
        columnIds,
        totalRows: input.totalImportRows,
        processedRows: processedRows + initialRows.length,
        onProgress: input.onProgress,
        syncProgress: input.syncProgress,
        signal: input.signal,
      });
      processedRows += initialRows.length + sourceRows;
      databaseBlockIds.push(provisioned.databaseBlockId);
      receipts.push(...(await scope.settle(input.signal)));
    } finally {
      await Promise.allSettled([scope.settle(input.signal)]);
      provisioned.dataSource.dispose();
    }
  }
  return {
    databaseBlockIds,
    receipts,
    writtenRows: processedRows - input.processedRows,
  };
}

function reportImportPreparing(
  onProgress: LocalShardedDatabaseImportProgressChange | undefined,
  totalImportRows: number
) {
  onProgress?.({
    phase: 'databases-preparing',
    processedRows: 0,
    totalRows: totalImportRows,
    percent: progressPercent(0, totalImportRows),
  });
}

function reportImportFinalizing(
  options: Pick<
    LocalShardedDatabaseImportOptions,
    'onProgress' | 'syncProgress' | 'signal'
  >,
  writtenRows: number,
  totalImportRows: number
) {
  throwIfLocalDatabaseImportAborted(options.signal);
  options.syncProgress?.savedLocally(writtenRows);
  options.onProgress?.({
    phase: 'import-finalizing',
    processedRows: totalImportRows,
    totalRows: totalImportRows,
    percent: 100,
  });
}

export async function importShardedDatabasesLocalFirst(
  options: LocalShardedDatabaseImportOptions
): Promise<LocalShardedDatabaseImportResult> {
  throwIfLocalDatabaseImportAborted(options.signal);
  const { doc, store, parentId } = ensureHostStore(options);
  throwIfLocalDatabaseImportAborted(options.signal);
  const databaseIdByClientId = databaseIdsByClientId(store, options.sources);
  const totalImportRows = options.sources.reduce(
    (sum, source) => sum + source.rows.length,
    0
  );
  const importRects = resolveImportSourceCanvasRects(
    options.sources,
    collectOccupiedCanvasBlockRects(store)
  );

  reportImportPreparing(options.onProgress, totalImportRows);
  const result = await importSourcesIntoStore({
    store,
    targets: options.sources.map((source, index) => {
      const databaseId = databaseIdByClientId.get(source.clientId);
      if (!databaseId) {
        throw new Error(
          `Database import clientId "${source.clientId}" missing.`
        );
      }
      const [x, y, width, height] = importRects[index]!;
      return {
        databaseId,
        source,
        parentId,
        props: { xywh: serializeXYWH(x, y, width, height) },
      };
    }),
    databaseIdByClientId,
    totalImportRows,
    processedRows: 0,
    onProgress: options.onProgress,
    syncProgress: options.syncProgress,
    signal: options.signal,
  });
  reportImportFinalizing(options, result.writtenRows, totalImportRows);

  return {
    docId: options.docId,
    doc,
    stats: {
      databaseCount: result.databaseBlockIds.length,
      totalRows: result.writtenRows,
    },
    databaseBlockIds: result.databaseBlockIds,
    receipts: result.receipts,
  };
}

/** A database whose host block a page import already placed in `docId`. */
export type LocalShardedHostBlockImportTarget = {
  docId: string;
  databaseId: string;
  source: DatabaseImportSourceContent;
};

export type LocalShardedHostBlockImportOptions = {
  collection: Workspace;
  targets: readonly LocalShardedHostBlockImportTarget[];
  extensions?: ExtensionType[];
  onProgress?: LocalShardedDatabaseImportProgressChange;
  syncProgress?: DatabaseImportSyncProgress;
  signal?: AbortSignal;
};

function groupTargetsByDoc(
  targets: readonly LocalShardedHostBlockImportTarget[]
): Map<string, LocalShardedHostBlockImportTarget[]> {
  const byDoc = new Map<string, LocalShardedHostBlockImportTarget[]>();
  for (const target of targets) {
    const group = byDoc.get(target.docId) ?? [];
    group.push(target);
    byDoc.set(target.docId, group);
  }
  return byDoc;
}

function hostBlockParentId(store: Store, databaseId: string): string {
  const host = store.getBlock(databaseId)?.model;
  if (!host || host.flavour !== 'affine:database') {
    throw new Error(
      `Database host block "${databaseId}" is missing in document "${store.id}".`
    );
  }
  const parent = store.getParent(host);
  if (!parent) {
    throw new Error(`Database host block "${databaseId}" has no parent.`);
  }
  return parent.id;
}

/**
 * Removes database host blocks a page import placed, for databases whose
 * storage was never written, so no page keeps a block naming an empty root.
 */
function removeHostBlocksWithoutStorage(input: {
  collection: Workspace;
  hosts: readonly { docId: string; databaseId: string }[];
  extensions?: ExtensionType[];
}): void {
  for (const { docId, databaseId } of input.hosts) {
    const doc = input.collection.getDoc(docId);
    if (!doc) continue;
    const store = doc.getStore({
      id: docId,
      ...(input.extensions ? { extensions: input.extensions } : {}),
    });
    const host = store.getBlock(databaseId)?.model;
    if (host) store.deleteBlock(host);
  }
}

/**
 * Writes the storage of databases whose host blocks a page import placed in
 * its pages. A host left without storage when the import fails is removed, so
 * no page keeps a database block that names a root nobody wrote.
 */
export async function importShardedDatabasesIntoHostBlocks(
  options: LocalShardedHostBlockImportOptions
): Promise<LocalShardedDatabaseImportResult[]> {
  const totalImportRows = options.targets.reduce(
    (sum, target) => sum + target.source.rows.length,
    0
  );
  const provisioned = new Set<string>();
  const results: LocalShardedDatabaseImportResult[] = [];
  let writtenRows = 0;
  try {
    throwIfLocalDatabaseImportAborted(options.signal);
    const databaseIdByClientId = new Map<string, string>();
    for (const target of options.targets) {
      if (databaseIdByClientId.has(target.source.clientId)) {
        throw new Error(
          `Duplicate database import clientId "${target.source.clientId}"`
        );
      }
      databaseIdByClientId.set(target.source.clientId, target.databaseId);
    }
    const stores = new Map(
      [...groupTargetsByDoc(options.targets).keys()].map(docId => [
        docId,
        ensureHostStore({
          collection: options.collection,
          docId,
          extensions: options.extensions,
        }),
      ])
    );
    reportImportPreparing(options.onProgress, totalImportRows);
    for (const [docId, targets] of groupTargetsByDoc(options.targets)) {
      const { doc, store } = stores.get(docId)!;
      const result = await importSourcesIntoStore({
        store,
        targets: targets.map(target => ({
          databaseId: target.databaseId,
          source: target.source,
          parentId: hostBlockParentId(store, target.databaseId),
        })),
        databaseIdByClientId,
        totalImportRows,
        processedRows: writtenRows,
        onProgress: options.onProgress,
        syncProgress: options.syncProgress,
        signal: options.signal,
        onProvisioned: databaseId => provisioned.add(databaseId),
      });
      writtenRows += result.writtenRows;
      results.push({
        docId,
        doc,
        stats: {
          databaseCount: result.databaseBlockIds.length,
          totalRows: result.writtenRows,
        },
        databaseBlockIds: result.databaseBlockIds,
        receipts: result.receipts,
      });
    }
  } catch (error) {
    removeHostBlocksWithoutStorage({
      collection: options.collection,
      hosts: options.targets.filter(
        target => !provisioned.has(target.databaseId)
      ),
      extensions: options.extensions,
    });
    throw error;
  }
  reportImportFinalizing(options, writtenRows, totalImportRows);
  return results;
}

export async function prepareShardedImportTargets(
  options: Omit<LocalShardedDatabaseImportFromBatchesOptions, 'readNextBatch'>
) {
  throwIfLocalDatabaseImportAborted(options.signal);
  const { doc, store, parentId } = ensureHostStore(options);
  throwIfLocalDatabaseImportAborted(options.signal);
  const databaseIdByClientId = databaseIdsByClientId(store, options.sources);
  const databaseBlockIds: string[] = [];
  const receipts: LocalPublicationReceipt[] = [];
  const totalImportRows = options.sources.reduce(
    (sum, source) => sum + source.rowCount,
    0
  );
  const dataSourcesByClientId = new Map<
    string,
    {
      dataSource: DatabaseBlockDataSource;
      columnIds: Map<string, string>;
    }
  >();
  const occupiedRects = collectOccupiedCanvasBlockRects(store);
  const importRects = resolveImportSourceCanvasRects(
    options.sources,
    occupiedRects
  );

  const ownedSources: DatabaseBlockDataSource[] = [];
  const scopes: ReturnType<DatabaseBlockDataSource['openPublicationScope']>[] =
    [];
  try {
    options.onProgress?.({
      phase: 'databases-preparing',
      processedRows: 0,
      totalRows: totalImportRows,
      percent: progressPercent(0, totalImportRows),
    });
    throwIfLocalDatabaseImportAborted(options.signal);

    for (const [sourceIndex, source] of options.sources.entries()) {
      throwIfLocalDatabaseImportAborted(options.signal);
      const databaseId = databaseIdByClientId.get(source.clientId);
      if (!databaseId) {
        throw new Error(
          `Database import clientId "${source.clientId}" missing.`
        );
      }
      const [x, y, width, height] = importRects[sourceIndex]!;
      let columnIds = new Map<string, string>();
      const provisioned = await provisionShardedDatabaseBlockDataSource(store, {
        databaseId,
        title: source.title,
        parentId,
        signal: options.signal,
        props: {
          xywh: serializeXYWH(x, y, width, height),
        },
        initDataSource: dataSource => {
          databaseViewInitTemplate(dataSource, 'table');
          columnIds = configureColumns({
            dataSource,
            source,
            databaseIdByClientId,
          });
        },
      });
      if (provisioned.status === 'discarded') continue;
      ownedSources.push(provisioned.dataSource);
      if (provisioned.receipt) receipts.push(provisioned.receipt);
      scopes.push(provisioned.dataSource.openPublicationScope());
      dataSourcesByClientId.set(source.clientId, {
        dataSource: provisioned.dataSource,
        columnIds,
      });
      databaseBlockIds.push(provisioned.databaseBlockId);
    }

    return {
      doc,
      store,
      databaseIdByClientId,
      databaseBlockIds,
      receipts,
      totalImportRows,
      dataSourcesByClientId,
      async dispose() {
        await Promise.allSettled(
          scopes.map(scope => scope.settle(options.signal))
        );
        for (const source of ownedSources) source.dispose();
      },
      async settle() {
        return (
          await Promise.all(scopes.map(scope => scope.settle(options.signal)))
        ).flat();
      },
    };
  } catch (error) {
    await Promise.allSettled(scopes.map(scope => scope.settle(options.signal)));
    for (const source of ownedSources) source.dispose();
    throw error;
  }
}
